import type { KycStatus } from '@miraki/contracts';

/**
 * R-18's KYC panel — the few decisions it makes, as pure functions so they are tested without a
 * browser. CR-57 / D105. Nothing here decides whether a retailer MAY order or file: that is the
 * server's, checked against the database on every request. This only decides what to SAY.
 */

export type KycStanding = 'NEEDED' | 'REVIEW' | 'APPROVED' | 'REJECTED';

/**
 * PENDING means two different things — nothing has been sent, or something has and Miraki has not
 * looked yet — and the retailer needs to know which, because one asks them to act and the other
 * asks them to wait. The status alone cannot say; the number of documents can.
 */
export function standing(status: KycStatus, documents: number): KycStanding {
  if (status === 'APPROVED') return 'APPROVED';
  if (status === 'REJECTED') return 'REJECTED';
  return documents === 0 ? 'NEEDED' : 'REVIEW';
}

/**
 * Whether Miraki's last rejection reason is shown, and how.
 *
 *   `rejected`  the account IS rejected: the reason leads the panel, and if somehow none was
 *               recorded the retailer is told to ask, rather than shown a blank.
 *   `earlier`   they have already filed again (so the status is back to PENDING) but Admin has not
 *               decided: the reason stays visible as the last thing Miraki said, until the next
 *               decision replaces it. Hiding it the moment they re-file would put them back in the
 *               position of not knowing whether the new file fixed the thing that was wrong.
 *   `none`      nothing to show — including APPROVED, where an old reason is just history.
 *
 * A retailer who does not know which file was wrong uploads the same one again, and then rings their
 * account manager. This is the function that stops that.
 */
export function reasonDisplay(status: KycStatus, reason: string | null): 'rejected' | 'earlier' | 'none' {
  if (status === 'APPROVED') return 'none';
  if (status === 'REJECTED') return 'rejected';
  return reason ? 'earlier' : 'none';
}

/** One decimal place, the way the server's refusals say it ("8.4MB"), so a message and a table agree. */
export function megabytes(bytes: number): string {
  return `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)}MB`;
}
